/**
 * P2-11 · The PLP filter URL contract — one place that encodes/decodes filter
 * state so the server (RSC) and client (rail/drawer/chips) agree byte-for-byte.
 *
 * URL shape: `?color=Red,Blue&size=M&price=20-50&brand=<id>&tag=summer&sale=1&availability=in_stock`.
 * Multi-value facets are comma lists; `price` is a `lo-hi` band in *major*
 * units (either bound may be blank); `sale`/`availability` are presence flags.
 * Option values carry their real catalogue casing (the swatch/checkbox facets
 * encode the value verbatim), so links our UI builds always round-trip.
 */
import type { MessageKey } from "~/i18n/messages";
import type { ListingFilters } from "~/server/api/schemas/listing";
import type { ListingAttributeFacet } from "~/types/product-card";

/** Every fixed query param this module owns — sort/view/page are left alone. */
export const FILTER_PARAM_KEYS = [
  "color",
  "size",
  "price",
  "brand",
  "tag",
  "sale",
  "availability",
] as const;

/**
 * P15-20 · Dynamic attribute facets are namespaced: `attr.<definition slug>`.
 *
 * The prefix is what makes the scheme collision-free. Attribute slugs are
 * brand data an operator invents, so an unprefixed `?material=Cotton` would one
 * day be a definition slugged `sort`, `page`, `view` or `color` quietly
 * hijacking a param that already means something. A `.`-separated namespace is
 * legal in a query key, survives `URLSearchParams` round-tripping unescaped,
 * and reserves exactly one prefix instead of the whole key space.
 *
 * Canonicals carry no query string by construction (`docs/seo.md` — `pathOf`
 * never emits one), so these params fragment no canonical and need no new
 * robots rule: they inherit the colour facet's SEO behaviour exactly.
 */
export const ATTR_PARAM_PREFIX = "attr.";

export function normaliseOptionName(name: string): string {
  const value = name.trim().toLowerCase();
  return value === "color" ? "colour" : value;
}

/** Reserved namespace retains arbitrary Unicode option names without collisions. */
export function optionParam(name: string): string {
  const key = normaliseOptionName(name);
  return key === "colour" ? "color" : key === "size" ? "size" : `opt.${key}`;
}

export function optionNameOfParam(param: string): string | null {
  if (!param.startsWith("opt.")) return null;
  const name = normaliseOptionName(param.slice(4));
  return name.length > 0 && name.length <= 50 && !/[\u0000-\u001f\u007f]/.test(name) ? name : null;
}


/** Slugs we accept from a URL. Anything else can't name a definition anyway. */
const ATTR_SLUG = /^[a-z0-9][a-z0-9._-]{0,63}$/i;

/** `attr.material` → `material`; anything else → null. */
export function attributeSlugOfParam(param: string): string | null {
  if (!param.startsWith(ATTR_PARAM_PREFIX)) return null;
  const slug = param.slice(ATTR_PARAM_PREFIX.length);
  return ATTR_SLUG.test(slug) ? slug.toLowerCase() : null;
}

/** `material` → `attr.material`. The one place the param name is composed. */
export function attributeParam(slug: string): string {
  return `${ATTR_PARAM_PREFIX}${slug}`;
}

/** Every key in the bag — the dynamic params can't be enumerated statically. */
function keysOf(params: SearchParamsInput): string[] {
  return params instanceof URLSearchParams
    ? [...new Set(params.keys())]
    : Object.keys(params);
}

/** A URL-ish bag: Next's `searchParams` or a `URLSearchParams`. */
export type SearchParamsInput =
  | URLSearchParams
  | Record<string, string | string[] | undefined>;

function first(params: SearchParamsInput, key: string): string | undefined {
  if (params instanceof URLSearchParams) return params.get(key) ?? undefined;
  const v = params[key];
  return Array.isArray(v) ? v[0] : v;
}

/** Split a comma list (`Red,Blue`) into trimmed, non-empty, de-duped values. */
export function csv(value: string | undefined): string[] {
  if (!value) return [];
  const out: string[] = [];
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

/**
 * The listing schema (`listingFiltersSchema`) bounds every facet list — max 50
 * values, each ≤100 chars. A crafted URL that overruns those bounds must
 * *degrade* (extra values ignored) rather than throw `BAD_REQUEST` and
 * error-page the whole PLP, matching this module's ignore-don't-error policy
 * (see `parsePriceBand`). Clamp here so the tRPC input is always valid.
 */
function clampFacetValues(values: string[]): string[] {
  return values.filter((v) => v.length <= 100).slice(0, 50);
}

/** `price=20-50` (major units) → minor bounds. Either bound may be blank. */
export function parsePriceBand(value: string | undefined): {
  priceMinMinor?: number;
  priceMaxMinor?: number;
} {
  if (!value) return {};
  const [lo, hi] = value.split("-");
  const toMinor = (s: string | undefined) => {
    if (!s || s.trim() === "") return undefined;
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : undefined;
  };
  const priceMinMinor = toMinor(lo);
  const priceMaxMinor = toMinor(hi);
  if (
    priceMinMinor !== undefined &&
    priceMaxMinor !== undefined &&
    priceMinMinor > priceMaxMinor
  ) {
    return {}; // incoherent band → ignore rather than error the page
  }
  return {
    ...(priceMinMinor !== undefined ? { priceMinMinor } : {}),
    ...(priceMaxMinor !== undefined ? { priceMaxMinor } : {}),
  };
}

/** Decode the URL into listing-query filters (server- and client-safe). */
export function parseFilterParams(params: SearchParamsInput): ListingFilters {
  const manufacturerIds = clampFacetValues(csv(first(params, "brand")));
  const tagSlugs = clampFacetValues(csv(first(params, "tag")));
  const colours = clampFacetValues(csv(first(params, "color")));
  const sizes = clampFacetValues(csv(first(params, "size")));
  const options: NonNullable<ListingFilters["options"]> = [];
  if (colours.length) options.push({ name: "colour", values: colours });
  if (sizes.length) options.push({ name: "size", values: sizes });
  for (const key of keysOf(params)) {
    const name = optionNameOfParam(key);
    if (!name || options.some((o) => o.name === name)) continue;
    const values = clampFacetValues(csv(first(params, key)));
    if (values.length && options.length < 10) options.push({ name, values });
  }

  // P15-20 · `attr.<slug>` params, in URL order, clamped to the schema's bounds
  // the same way the fixed facets are: a crafted URL degrades, never errors.
  const attributes: NonNullable<ListingFilters["attributes"]> = [];
  for (const key of keysOf(params)) {
    const slug = attributeSlugOfParam(key);
    if (slug === null || attributes.some((a) => a.slug === slug)) continue;
    const raw = first(params, key);
    const numeric = parseAttributeNumberBand(raw);
    if (numeric) {
      attributes.push({ slug, values: [], ...numeric });
      continue;
    }
    if (raw?.startsWith("~")) continue;
    const values = clampFacetValues(csv(raw));
    if (values.length) attributes.push({ slug, values });
  }

  return {
    ...parsePriceBand(first(params, "price")),
    ...(first(params, "sale") === "1" ? { onSale: true } : {}),
    ...(first(params, "availability") === "in_stock" ? { inStock: true } : {}),
    ...(manufacturerIds.length ? { manufacturerIds } : {}),
    ...(tagSlugs.length ? { tagSlugs } : {}),
    ...(options.length ? { options } : {}),
    ...(attributes.length ? { attributes: attributes.slice(0, 10) } : {}),
  };
}

/** Format a price band back to its `lo-hi` param (major units), or undefined. */
export function priceBandParam(filters: ListingFilters): string | undefined {
  const lo = filters.priceMinMinor;
  const hi = filters.priceMaxMinor;
  if (lo === undefined && hi === undefined) return undefined;
  const major = (m: number | undefined) => (m === undefined ? "" : String(m / 100));
  return `${major(lo)}-${major(hi)}`;
}

function optionValues(filters: ListingFilters, name: string): string[] {
  return (
    filters.options?.find((o) => normaliseOptionName(o.name) === normaliseOptionName(name))?.values ?? []
  );
}

/** Encode filters back to query params (inverse of `parseFilterParams`). */
export function filtersToSearch(filters: ListingFilters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const option of filters.options ?? []) {
    if (option.values.length) out[optionParam(option.name)] = option.values.join(",");
  }
  const price = priceBandParam(filters);
  if (price) out.price = price;
  if (filters.manufacturerIds?.length) out.brand = filters.manufacturerIds.join(",");
  if (filters.tagSlugs?.length) out.tag = filters.tagSlugs.join(",");
  if (filters.onSale) out.sale = "1";
  if (filters.inStock) out.availability = "in_stock";
  for (const a of filters.attributes ?? []) {
    const param = attributeParam(a.slug);
    if (a.minNumber !== undefined || a.maxNumber !== undefined) {
      out[param] = `~${a.minNumber ?? ""}..${a.maxNumber ?? ""}`;
    } else if (a.values.length) out[param] = a.values.join(",");
  }
  return out;
}

/** The values selected for one attribute definition, by slug. */
function attributeValues(filters: ListingFilters, slug: string): string[] {
  return (
    filters.attributes?.find((a) => a.slug === slug.toLowerCase())?.values ?? []
  );
}

/** True when no filter is active (used to gate chips / the filter-empty state). */
export function hasActiveFilters(filters: ListingFilters): boolean {
  return Object.keys(filtersToSearch(filters)).length > 0;
}

/** Whether a given (param, value) is currently active — drives control state. */
export function isFilterSelected(
  filters: ListingFilters,
  param: string,
  value: string,
): boolean {
  switch (param) {
    case "color":
      return optionValues(filters, "colour").includes(value);
    case "size":
      return optionValues(filters, "size").includes(value);
    case "brand":
      return (filters.manufacturerIds ?? []).includes(value);
    case "tag":
      return (filters.tagSlugs ?? []).includes(value);
    case "sale":
      return filters.onSale === true;
    case "availability":
      return filters.inStock === true;
    default: {
      const option = optionNameOfParam(param);
      if (option) return optionValues(filters, option).includes(value);
      const slug = attributeSlugOfParam(param);
      return slug !== null && attributeValues(filters, slug).includes(value);
    }
  }
}

// ─── Active-filter chips ─────────────────────────────────────────────────────

/** One removable chip. `param`+`value` identify what to strip from the URL. */
export interface FilterChip {
  id: string;
  label: string;
  /**
   * P15-20 · Widened from the fixed key union: a dynamic attribute chip carries
   * `attr.<slug>`, which no closed union can spell.
   */
  param: (typeof FILTER_PARAM_KEYS)[number] | string;
  /** Present for multi-value params (remove one value); absent clears the param. */
  value?: string;
  /**
   * P15-20 · A message key for the VALUE half of the label, rendered as
   * `${label}: ${t(valueLabelKey)}`. Only BOOLEAN attribute values need it —
   * `true`/`false` are storage, not copy, and must read as Yes/No in the
   * shopper's language.
   */
  valueLabelKey?: MessageKey;
}

export interface ChipContext {
  /** Manufacturer id → display name, to label `brand` chips. */
  manufacturerNames?: Map<string, string>;
  /** Currency symbol for the price chip (defaults to none). */
  currencySymbol?: string;
  /**
   * P15-20 · Attribute slug → definition, from the facet counts, so an
   * `attr.*` chip reads "Material: Cotton" rather than "material: Cotton".
   * Absent (or missing an entry) degrades to the slug — a chip that still
   * removes the right param.
   */
  attributes?: Map<string, { name: string; type: AttributeFacetType; unit?: string | null }>;
}

/** The attribute types that can carry a facet (NUMBER wants a range control). */
export type AttributeFacetType = ListingAttributeFacet["type"];

function priceChipLabel(filters: ListingFilters, symbol: string): string {
  const lo = filters.priceMinMinor;
  const hi = filters.priceMaxMinor;
  const fmt = (m: number) => `${symbol}${m / 100}`;
  if (lo !== undefined && hi !== undefined) return `${fmt(lo)} – ${fmt(hi)}`;
  if (lo !== undefined) return `${fmt(lo)}+`;
  if (hi !== undefined) return `Under ${fmt(hi)}`;
  return "";
}

/** Derive the active-filter chip row — one chip per encoded value (P2-DB02 §03). */
export function buildActiveChips(
  filters: ListingFilters,
  ctx: ChipContext = {},
): FilterChip[] {
  const chips: FilterChip[] = [];
  const symbol = ctx.currencySymbol ?? "";

  for (const option of filters.options ?? []) {
    const param = optionParam(option.name);
    for (const value of option.values) {
      chips.push({ id: `${param}:${value}`, label: value, param, value });
    }
  }
  for (const id of filters.manufacturerIds ?? []) {
    chips.push({
      id: `brand:${id}`,
      label: ctx.manufacturerNames?.get(id) ?? id,
      param: "brand",
      value: id,
    });
  }
  for (const slug of filters.tagSlugs ?? []) {
    chips.push({ id: `tag:${slug}`, label: `#${slug}`, param: "tag", value: slug });
  }
  if (filters.priceMinMinor !== undefined || filters.priceMaxMinor !== undefined) {
    chips.push({ id: "price", label: priceChipLabel(filters, symbol), param: "price" });
  }
  if (filters.onSale) chips.push({ id: "sale", label: "On sale", param: "sale" });
  if (filters.inStock) {
    chips.push({ id: "availability", label: "In stock", param: "availability" });
  }
  // P15-20 · Dynamic attribute chips, last — the rail renders their groups last
  // too, so the chip row reads in the same order as the controls above it.
  for (const attr of filters.attributes ?? []) {
    const def = ctx.attributes?.get(attr.slug);
    const name = def?.name ?? attr.slug;
    if (attr.minNumber !== undefined || attr.maxNumber !== undefined) {
      const lo = attr.minNumber === undefined ? "Any" : String(attr.minNumber);
      const hi = attr.maxNumber === undefined ? "Any" : String(attr.maxNumber);
      chips.push({
        id: `${attributeParam(attr.slug)}:range`,
        label: `${name}: ${lo}–${hi}${def?.unit ? ` ${def.unit}` : ""}`,
        param: attributeParam(attr.slug),
      });
      continue;
    }
    for (const value of attr.values) {
      const param = attributeParam(attr.slug);
      chips.push({
        id: `${param}:${value}`,
        label: def?.type === "BOOLEAN" ? name : `${name}: ${value}`,
        ...(def?.type === "BOOLEAN"
          ? { valueLabelKey: booleanValueKey(value) }
          : {}),
        param,
        value,
      });
    }
  }
  return chips;
}

/** `"true"`/`"false"` → the Yes/No message key the renderer resolves. */
export function booleanValueKey(value: string): MessageKey {
  return value === "true" ? "browse.facetYes" : "browse.facetNo";
}

// ─── Param mutation (client-side URL edits) ─────────────────────────────────

/**
 * Return a new query string with a filter removed and `page` reset (any filter
 * change returns to page 1 — P2-11). Omitting `value` clears the whole param;
 * providing it removes just that value from a comma list. Non-filter params
 * (sort, view) are preserved.
 */
export function removeFilterValue(
  search: string,
  param: string,
  value?: string,
): string {
  const params = new URLSearchParams(search);
  if (value === undefined) {
    params.delete(param);
  } else {
    const remaining = csv(params.get(param) ?? undefined).filter((v) => v !== value);
    if (remaining.length) params.set(param, remaining.join(","));
    else params.delete(param);
  }
  params.delete("page");
  return params.toString();
}

/** Strip every filter param (keep sort/view), resetting to page 1. */
export function clearAllFilters(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of FILTER_PARAM_KEYS) params.delete(key);
  // P15-20 · The dynamic half. "Clear all" that left `attr.*` behind would be a
  // lie the chip row would then contradict, so the prefix is swept too.
  for (const key of [...new Set(params.keys())]) {
    if (attributeSlugOfParam(key) !== null || optionNameOfParam(key) !== null) params.delete(key);
  }
  params.delete("page");
  return params.toString();
}

/**
 * Toggle one value of a multi-value facet (color/size/brand, or an
 * `attr.<slug>` attribute facet) in the URL, resetting to page 1.
 */
export function toggleFilterValue(
  search: string,
  param: "color" | "size" | "brand" | string,
  value: string,
): string {
  const params = new URLSearchParams(search);
  if (attributeSlugOfParam(param) && params.get(param)?.startsWith("~")) {
    params.delete(param);
  }
  const values = csv(params.get(param) ?? undefined);
  const next = values.includes(value)
    ? values.filter((v) => v !== value)
    : [...values, value];
  if (next.length) params.set(param, next.join(","));
  else params.delete(param);
  params.delete("page");
  return params.toString();
}

/** Set/clear a presence flag (sale/availability), resetting to page 1. */
export function setFlagParam(
  search: string,
  param: "sale" | "availability",
  on: boolean,
): string {
  const params = new URLSearchParams(search);
  if (on) params.set(param, param === "sale" ? "1" : "in_stock");
  else params.delete(param);
  params.delete("page");
  return params.toString();
}

/** Set/clear the price band (major-unit bounds), resetting to page 1. */
export function setPriceBand(
  search: string,
  loMajor: number | undefined,
  hiMajor: number | undefined,
): string {
  const params = new URLSearchParams(search);
  const lo = loMajor === undefined || loMajor <= 0 ? "" : String(loMajor);
  const hi = hiMajor === undefined ? "" : String(hiMajor);
  if (lo === "" && hi === "") params.delete("price");
  else params.set("price", `${lo}-${hi}`);
  params.delete("page");
  return params.toString();
}

/** Decode the numeric attr syntax (`~min..max`); malformed bands degrade away. */
export function parseAttributeNumberBand(value: string | undefined): {
  minNumber?: number;
  maxNumber?: number;
} | null {
  if (!value?.startsWith("~")) return null;
  const match = /^~(-?(?:\d+(?:\.\d+)?))?\.\.(-?(?:\d+(?:\.\d+)?))?$/.exec(value);
  if (!match || (!match[1] && !match[2])) return null;
  const minNumber = match[1] === undefined ? undefined : Number(match[1]);
  const maxNumber = match[2] === undefined ? undefined : Number(match[2]);
  if (
    (minNumber !== undefined && !Number.isFinite(minNumber)) ||
    (maxNumber !== undefined && !Number.isFinite(maxNumber)) ||
    (minNumber !== undefined && maxNumber !== undefined && minNumber > maxNumber)
  ) return null;
  return {
    ...(minNumber !== undefined ? { minNumber } : {}),
    ...(maxNumber !== undefined ? { maxNumber } : {}),
  };
}

/** Set/clear one numeric attribute band, preserving all other facets. */
export function setAttributeNumberBand(
  search: string,
  param: string,
  lo: number | undefined,
  hi: number | undefined,
): string {
  const params = new URLSearchParams(search);
  const slug = attributeSlugOfParam(param);
  if (!slug) return params.toString();
  const validLo = lo !== undefined && Number.isFinite(lo) ? lo : undefined;
  const validHi = hi !== undefined && Number.isFinite(hi) ? hi : undefined;
  if (validLo !== undefined && validHi !== undefined && validLo > validHi) {
    return params.toString();
  }
  if (validLo === undefined && validHi === undefined) params.delete(param);
  else params.set(param, `~${validLo ?? ""}..${validHi ?? ""}`);
  params.delete("page");
  return params.toString();
}
